import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { ChevronDown } from 'lucide-react';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import GridWrapper from '../../shared/components/layout/GridWrapper';
import PaginationComponent from '../../shared/components/PaginationComponent';
import NoDataFound from '../components/layout/NoDataFound';
import Search from '../../shared/components/ui/Search';
import {
  getAccountsPriorities,
  updateAccountsPriorities,
  getLandById
} from '../services/api/implementation/impl';
import Loader from '../../shared/components/ui/Loader';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import NoDataFoundImg from '../../shared/assets/png/noDataFound.png';
import Modal from '../../shared/components/ui/Modal';
import Button from '../../shared/components/ui/Button';
import FormInput from '../../shared/components/ui/FormInput';
import { useForm } from 'react-hook-form';
import { useProjectsList } from '../../../contexts/ProjectsContext';

const Priorities = () => {
  const userId = Cookies.get('userId');
  const { collapsed } = useSidebar();
  const { projects, projectsForDropdown } = useProjectsList();

  // State management
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [selectedPriority, setSelectedPriority] = useState(null);

  // Filter states
  const [projectFilter, setProjectFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Dropdown states
  const [openDropdown, setOpenDropdown] = useState(null);
  const projectHeaderRef = useRef(null);
  const statusHeaderRef = useRef(null);
  const [projectDropdownRect, setProjectDropdownRect] = useState(null);
  const [statusDropdownRect, setStatusDropdownRect] = useState(null);

  const {
    data: prioritiesData,
    refetch: refetchPriorities,
    isLoading
  } = useQuery({
    queryKey: ['orderActivities', page, projectFilter, statusFilter, searchQuery],
    queryFn: () => getAccountsPriorities(page, limit, projectFilter, statusFilter, searchQuery),
    keepPreviousData: true
  });

  // Handle pagination
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Mutation for updating priority status
  const { mutate: updatePriorityStatus, isPending: isGeneratingInvoice } = useMutation({
    mutationFn: (payload) => updateAccountsPriorities(selectedPriority?.id, payload),
    onSuccess: async () => {
      toast.success('Invoice generated successfully');
      await refetchPriorities();
      handleCloseInvoiceModal();
    },
    onError: (error) => {
      console.error('Error generating invoice:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to generate invoice');
    }
  });

  // Form handling for invoice modal
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors }
  } = useForm();

  const invoiceType = watch('invoiceType');
  const [landOwners, setLandOwners] = useState([]);
  const [loadingLandOwners, setLoadingLandOwners] = useState(false);

  useEffect(() => {
    const fetchLandDetails = async () => {
      if (invoiceType === 'Owner Based' && selectedPriority?.deal?.unit?.block?.project?.land_id) {
        try {
          setLoadingLandOwners(true);
          const landId = selectedPriority.deal.unit.block.project.land_id;
          const landData = await getLandById(landId);

          if (landData?.ownerLandMappings) {
            const owners = landData.ownerLandMappings.map(mapping => ({
              label: mapping.owner?.name || 'Unknown Owner',
              value: mapping.owner?.id
            }));
            setLandOwners(owners);
          }
        } catch (error) {
          console.error('Error fetching land details:', error);
          toast.error('Failed to fetch land owners');
        } finally {
          setLoadingLandOwners(false);
        }
      } else {
        setLandOwners([]);
        setValue('landOwner', '');
      }
    };

    fetchLandDetails();
  }, [invoiceType, selectedPriority, setValue]);

  // Handle priority action button click - open modal
  const handleGenerateInvoice = (priority) => {
    setSelectedPriority(priority);
    setIsInvoiceModalOpen(true);
  };

  // Close invoice modal
  const handleCloseInvoiceModal = () => {
    setIsInvoiceModalOpen(false);
    setSelectedPriority(null);
    setLandOwners([]);
    reset();
  };

  // Handle invoice form submission
  const onSubmitInvoice = async (data) => {
    const isOwnerBased = data.invoiceType === 'Owner Based';

    const payload = {
      invoiceNumber: data.invoiceNumber,
      issueDate: data.issueDate,
      companyBased: !isOwnerBased
    };

    if (isOwnerBased) {
      if (!data.landOwner) {
        toast.error('Please select a land owner');
        return;
      }
      payload.land_owner_id = data.landOwner;
    }

    console.log('Invoice Payload:', payload);

    updatePriorityStatus(payload);
  };

  useEffect(() => {
    setPage(1); // Reset to page 1 when filters change
    refetchPriorities();
  }, [projectFilter, statusFilter, searchQuery]);

  useEffect(() => {
    refetchPriorities();
  }, [page]);

  // Search handler
  const handleSearchChange = useCallback((incomingQuery) => {
    setPage(1);
    setSearchQuery(incomingQuery);
  }, []);

  const handleOnClickSearch = async () => {
    await refetchPriorities();
  };

  // Track page view
  useEffect(() => {
    mixpanel.track('Priorities Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  // Status options for filter
  const statusOptions = [
    { id: 'Completed', label: 'Completed', value: 'Completed' },
    { id: 'Pending', label: 'Pending', value: 'Pending' }
  ];

  // Handler to open dropdown and set position
  const handleHeaderDropdownClick = (type, event) => {
    event.preventDefault();
    event.stopPropagation();

    // If clicking the same dropdown that's already open, close it
    if (openDropdown === type) {
      setOpenDropdown(null);
      return;
    }

    if (type === 'project' && projectHeaderRef?.current) {
      setProjectDropdownRect(projectHeaderRef?.current?.getBoundingClientRect());
    }
    if (type === 'status' && statusHeaderRef?.current) {
      setStatusDropdownRect(statusHeaderRef?.current?.getBoundingClientRect());
    }
    setOpenDropdown(type);
  };

  // Handler to select item
  const handleSelectDropdownItem = (type, value) => {
    setOpenDropdown(null);

    if (type === 'project') {
      if (!value) {
        setProjectFilter('');
      } else {
        setProjectFilter(value);
      }
    } else if (type === 'status') {
      if (!value) {
        setStatusFilter('');
      } else {
        setStatusFilter(value);
      }
    }
  };

  // Add a global click handler to close dropdown when clicking outside
  useEffect(() => {
    if (!openDropdown) return;

    const handleClick = (event) => {
      // Don't close if clicking inside a dropdown or on dropdown trigger
      if (
        event.target.closest('[data-dropdown-trigger]') ||
        event.target.closest('.absolute.bg-white.border.rounded.shadow')
      ) {
        return;
      }
      setOpenDropdown(null);
    };

    // Use capture phase to ensure it runs before other click handlers
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [openDropdown]);

  const displayData = prioritiesData?.data || [];
  const totalPages = Math.ceil((prioritiesData?.meta?.total || 0) / limit);

  return (
    <>
      <Loader isVisible={isLoading} />

      <Layout>
        {/* Header */}
        <div
          className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
            }`}>
          <Header data={{ name: 'Priorities' }} />{' '}
          <div
            className="fixed z-[9] bg-white border-b border-gray-200 shadow-sm px-6 py-4 transition-all duration-300 ease-in-out"
            style={{
              top: '4.1rem',
              left: collapsed ? '76px' : '16.6%',
              right: '0'
            }}>
            <div className="flex items-center justify-between">
              <Search
                onChange={handleSearchChange}
                query={searchQuery}
                onClick={handleOnClickSearch}
                placeholder="Search priorities..."
              />
            </div>
          </div>
        </div>

        {/* Main content */}
        {displayData && displayData.length > 0 ? (
          <GridWrapper width={100}>
            <div className="mt-[10rem] relative h-[calc(100vh-11rem)]   overflow-auto rounded-t-lg flex flex-col items-center gap-4">
              <div className="overflow-x-auto w-full relative rounded-t-lg">
                <table className="text-left w-full ">
                  <thead className="bg-gray-200 !text-gray-600 w-full sticky top-0 z-20">
                    <tr className="font-normal h-[3rem] w-full">
                      <th className="px-4 py-2 border-b font-normal">Title</th>
                      <th className="px-4 py-2 border-b font-normal">Stage Name</th>
                      <th
                        className="px-4 py-2 border-b font-normal relative"
                        ref={projectHeaderRef}>
                        <span>Project Name</span>
                        <button
                          className="ml-1 align-middle"
                          data-dropdown-trigger
                          onClick={(e) => {
                            e.stopPropagation();
                            handleHeaderDropdownClick('project', e);
                          }}>
                          <ChevronDown size={16} />
                        </button>
                        {openDropdown === 'project' && (
                          <div
                            className="absolute bg-white border rounded shadow z-[1000] top-full left-0 min-w-full"
                            style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              minWidth: '100%',
                              maxHeight: '250px',
                              overflowY: 'auto'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}>
                            {[
                              {
                                id: '',
                                value: '',
                                label: projectFilter ? 'Clear Filter' : 'Select Project',
                                selected: true
                              },
                              ...projectsForDropdown
                            ].map((proj, idx) => (
                              <div
                                key={proj.id || idx}
                                className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${proj.id === '' && 'bg-gray-100'
                                  } ${proj.id === projectFilter && 'bg-blue-100'}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleSelectDropdownItem('project', proj.id);
                                }}>
                                {proj.label}
                              </div>
                            ))}
                          </div>
                        )}
                      </th>
                      <th className="px-4 py-2 border-b font-normal">Lead Name</th>
                      <th className="px-4 py-2 border-b font-normal">Block - Unit</th>
                      <th className="px-4 py-2 border-b font-normal">Total Amount</th>
                      <th className="px-4 py-2 border-b font-normal">Date</th>
                      <th className="px-4 py-2 border-b font-normal relative" ref={statusHeaderRef}>
                        <span>Status</span>
                        <button
                          className="ml-1 align-middle"
                          data-dropdown-trigger
                          onClick={(e) => {
                            e.stopPropagation();
                            handleHeaderDropdownClick('status', e);
                          }}>
                          <ChevronDown size={16} />
                        </button>
                        {openDropdown === 'status' && (
                          <div
                            className="absolute bg-white border rounded shadow z-[1000] top-full left-0 min-w-full"
                            style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              minWidth: '100%',
                              maxHeight: '250px',
                              overflowY: 'auto'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}>
                            {[
                              {
                                id: '',
                                value: '',
                                label: statusFilter ? 'Clear Filter' : 'Select Status',
                                selected: true
                              },
                              ...statusOptions
                            ].map((status, idx) => (
                              <div
                                key={status.id || idx}
                                className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${status.value === '' && 'bg-gray-100'
                                  } ${status.value === statusFilter && 'bg-blue-100'}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleSelectDropdownItem('status', status.value);
                                }}>
                                {status.label}
                              </div>
                            ))}
                          </div>
                        )}
                      </th>
                      <th className="px-4 py-2 border-b font-normal">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayData.length > 0 ? (
                      displayData.map((priority, index) => (
                        <tr
                          key={priority.id || index}
                          className="border-b hover:bg-gray-50 bg-white">
                          <td className="px-4 py-2 text-sm">
                            <div className="flex flex-col">
                              <span className="font-medium">{priority.title || '---'}</span>
                            </div>
                          </td>
                          <td className="px-4 py-2 text-sm">
                            <div className="flex flex-col">
                              <span className="font-medium">{priority.stageName || '---'}</span>
                              <span className="text-xs text-gray-500">
                                {priority.stagePercentage ? `${priority.stagePercentage}%` : '---'}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-2 text-sm">
                            {priority.deal?.unit?.block?.project?.name || '---'}
                          </td>
                          <td className="px-4 py-2 text-sm">
                            {priority.deal?.lead?.user?.name || '---'}
                          </td>
                          <td className="px-4 py-2 text-sm">
                            {priority.deal?.unit?.block?.name && priority.deal?.unit?.unitName
                              ? `${priority.deal.unit.block.name} - ${priority.deal.unit.unitName}`
                              : '---'}
                          </td>
                          <td className="px-4 py-2 text-sm">
                            <div className="flex flex-col">
                              <span className="font-medium text-green-600">
                                ₹
                                {priority.deal?.total_price
                                  ? new Intl.NumberFormat('en-IN').format(priority.deal.total_price)
                                  : '---'}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-2 text-sm">
                            {priority.date
                              ? new Date(priority.date).toLocaleDateString('en-IN')
                              : '---'}
                          </td>
                          <td className="px-4 py-2 text-sm">
                            <div className="relative z-0">
                              <span className="flex pl-6 overflow-hidden text-sm">
                                {priority.status || '---'}
                              </span>
                              <div
                                className={`w-[5px] h-[100%] rounded-t-2xl rounded-r-[3rem] absolute left-[4px] top-[-2px] ${priority.status === 'Completed'
                                  ? 'bg-[#3F8D78]' // Green for completed
                                  : priority.status === 'Pending'
                                    ? 'bg-[#E97B37]' // Orange for pending
                                    : priority.status === 'In Progress'
                                      ? 'bg-[#3B82F6]' // Blue for in progress
                                      : 'bg-gray-400' // Default gray
                                  }`}>
                                &nbsp;
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-2 text-sm">
                            <div className="flex gap-2">
                              {priority.status === 'Pending' && (
                                <button
                                  onClick={() => handleGenerateInvoice(priority)}
                                  disabled={isGeneratingInvoice}
                                  className={`px-3 py-1 text-white text-xs rounded transition-all ${isGeneratingInvoice
                                    ? 'bg-gray-400 cursor-not-allowed opacity-60'
                                    : 'bg-[#2a565e] hover:bg-[#1f4148]'
                                    }`}>
                                  {isGeneratingInvoice && selectedPriority?.id === priority.id
                                    ? 'Processing...'
                                    : 'Generate Invoice'}
                                </button>
                              )}
                              {priority.status === 'Completed' && (
                                <span className="px-3 py-1 text-black text-xs rounded">
                                  Invoice Generated
                                </span>
                              )}
                              {!['Pending', 'Completed'].includes(priority.status) && (
                                <span className="text-gray-500 text-xs">No actions</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={9} className="text-center py-10 align-middle">
                          <div className="flex flex-col items-center justify-center w-full h-full">
                            <img
                              src={NoDataFoundImg}
                              alt="No Data"
                              className="w-32 h-28 mx-auto mb-2 object-contain"
                            />
                            <h2 className="text-lg font-bold text-gray-800 mb-1">No Data Found</h2>
                            <p className="text-gray-600 text-sm">No priorities data available.</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {prioritiesData?.data && prioritiesData.data.length > 0 && (
                <PaginationComponent
                  page={page}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  isFetching={isLoading}
                />
              )}
            </div>
          </GridWrapper>
        ) : (
          <NoDataFound buttonText="Refresh" onButtonClick={() => refetchPriorities()} />
        )}
      </Layout>

      {/* Invoice Generation Modal */}
      {isInvoiceModalOpen && (
        <Modal name="Generate Invoice" onClose={handleCloseInvoiceModal} width={40} isDirty={false}>
          <div>
            <div className="mb-4">
              <p className="text-xs text-gray-500 mt-1">
                Lead: {selectedPriority?.deal?.lead?.user?.name} | Unit:{' '}
                {selectedPriority?.deal?.unit?.unitName}
              </p>
            </div>

            <form onSubmit={handleSubmit(onSubmitInvoice)} className="space-y-4">
              <div>
                <FormInput
                  label="Invoice Number"
                  isRequired
                  register_key="invoiceNumber"
                  register={register}
                  type="text"
                  placeholder="Enter invoice number"
                  errors={errors.invoiceNumber}
                  width={100}
                />
              </div>

              <div>
                <FormInput
                  label="Issue Date"
                  isRequired
                  register_key="issueDate"
                  register={register}
                  type="date"
                  errors={errors.issueDate}
                  width={100}
                  CustomFeature={{
                    max: new Date().toISOString().split('T')[0]
                  }}
                />
              </div>

              <div>
                <FormInput
                  label="Invoice Type"
                  isRequired
                  register_key="invoiceType"
                  register={register}
                  type="dropdown"
                  options={[
                    { label: 'Company Based', value: 'Company Based' },
                    { label: 'Owner Based', value: 'Owner Based' }
                  ]}
                  errors={errors.invoiceType}
                  width={100}
                />
              </div>

              {invoiceType === 'Owner Based' && (
                <div>
                  {loadingLandOwners ? (
                    <div className="text-sm text-gray-500">Loading land owners...</div>
                  ) : (
                    <FormInput
                      label="Land Owner"
                      isRequired
                      register_key="landOwner"
                      register={register}
                      type="dropdown"
                      options={[
                        { label: 'Select Land Owner', value: '' },
                        ...landOwners
                      ]}
                      errors={errors.landOwner}
                      width={100}
                    />
                  )}
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  onClick={handleCloseInvoiceModal}
                  displayType="secondary"
                  disabled={isGeneratingInvoice}>
                  Cancel
                </Button>
                <Button type="submit" displayType="primary" disabled={isGeneratingInvoice}>
                  {isGeneratingInvoice ? 'Generating...' : 'Generate Invoice'}
                </Button>
              </div>
            </form>
          </div>
        </Modal>
      )}
    </>
  );
};

export default Priorities;
